import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  memo,
  useMemo,
} from "react";
import {
  TextField,
  Button,
  List,
  ListItem,
  FormControl,
  Popover,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { UserContext } from "../../auth/auth-context";
import { rootClientPath } from "../../../util/link";
import { useSelectedBusiness } from "../../../redux/store";
import {
  SEND_EMAIL_MUTATION,
  UPDATE_PAYPAL_SUBSCRIPTION,
} from "../../../graphql/mutations";

// Generic dialog wrapper
const GenericDialog = ({ open, handleClose, title, content, actions }) => (
  <Dialog open={open} onClose={handleClose}>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>{content}</DialogContent>
    <DialogActions>{actions}</DialogActions>
  </Dialog>
);

// Core content of the staff dialog
const AddEditStaffContent = memo(
  ({
    username,
    setUsername,
    url,
    setUrl,
    email,
    setEmail,
    role,
    handlePermissionClick,
    anchorEl,
    isPopoverOpen,
    selectedPermission,
    handlePermissionChange,
    selectedBusiness,
    isEdit,
    usernameError,
    emailError,
    urlError,
  }) => (
    <>
      <TextField
        label="Name"
        value={username}
        variant="outlined"
        fullWidth
        onChange={(e) => setUsername(e.target.value)}
        error={usernameError}
        helperText={usernameError ? "Name is required" : ""}
        style={{ marginBottom: "16px", marginTop: "16px" }}
      />
      <TextField
        label="Email"
        value={email}
        variant="outlined"
        fullWidth
        onChange={(e) => setEmail(e.target.value)}
        error={emailError}
        helperText={emailError ? "Invalid email address" : ""}
        style={{ marginBottom: "16px" }}
      />
      {isEdit && (
        <TextField
          label="URL"
          value={url}
          variant="outlined"
          fullWidth
          onChange={(e) => setUrl(e.target.value)}
          error={urlError}
          helperText={
            urlError
              ? "URL can only contain letters, numbers, underscores and dots"
              : ""
          }
          style={{ marginBottom: "16px", marginTop: "16px" }}
        />
      )}
      <FormControl fullWidth>
        <TextField
          label="Role"
          variant="outlined"
          onClick={handlePermissionClick}
          aria-describedby={anchorEl ? "role-popover" : undefined}
          fullWidth
          InputProps={{
            readOnly: true,
            endAdornment: (
              <div>
                {isPopoverOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </div>
            ),
          }}
          value={selectedPermission ? selectedPermission.name : role}
          style={{ marginBottom: "16px" }}
        />
        <Popover
          id="role-popover"
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={() => handlePermissionChange(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <List>
            {selectedBusiness?.roles?.map((roleItem) => (
              <ListItem
                key={roleItem.id}
                button
                onClick={() => handlePermissionChange(roleItem)}
              >
                <ListItemText
                  primary={roleItem.name}
                  secondary={roleItem.description}
                />
              </ListItem>
            ))}
          </List>
        </Popover>
      </FormControl>
    </>
  )
);

const AddEditStaffStyledDialog = ({
  open,
  handleClose,
  staff,
  handleSaveUpdateStaff,
}) => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(3);
  const [url, setUrl] = useState("");
  const [role, setRole] = useState("");
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const selectedBusiness = useSelectedBusiness();
  const [selectedPermission, setSelectedPermission] = useState(null);
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);
  const [updatePaypalSubscription] = useMutation(UPDATE_PAYPAL_SUBSCRIPTION);
  const prevOpenRef = useRef(false);

  // Initialize fields
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      if (staff) {
        setUsername(staff?.user?.username ?? "");
        setUrl(staff?.url ?? "");
        setEmail(staff?.user?.email || "");
        setRole(staff?.role?.name ?? "");
        setStatus(staff?.status ?? "");
      } else {
        setUsername("");
        setUrl("");
        setEmail("");
        setRole("");
        setStatus(3);
      }
    }
    prevOpenRef.current = open;
  }, [open, staff]);

  // Validations
  const isEmailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), [email]);
  const isUrlValid = useMemo(
    () => !staff || /^[a-zA-Z0-9_.]+$/.test(url.trim()),
    [url, staff]
  );
  const isRoleValid = staff ? true : selectedPermission !== null;

  const usernameError = username.trim() === "";
  const emailError = email.trim() !== "" && !isEmailValid;
  const urlError = staff && url.trim() !== "" && !isUrlValid;

  const isFormValid =
    !usernameError && !emailError && !urlError && isRoleValid;

  const handleSave = async () => {
    const editedStaff = {
      ...staff,
      status,
      url,
      user: {
        ...staff?.user,
        email,
        username,
      },
      role: {
        id: Number(selectedPermission?.id ?? staff?.role?.id),
        name: selectedPermission?.name ?? staff?.role?.name,
      },
    };

    const updatedStaff = await handleSaveUpdateStaff(editedStaff);
    handleClose();

    if (updatedStaff) {
      const link = `${rootClientPath}/biz/approve/${updatedStaff?.staffApproveId}`;

      const emailBodyNewStaff = `
        <p>Hello, ${username}</p>
        <p>You're invited to join Plany365 system for ${selectedBusiness?.businessBasic.name}.</p>
        <p><a href="${link}">Click here to sign up</a>.</p>`;

      const emailBodyBusinessOwner = `
        <p>Hello, ${providerUser.name},</p>
        <p>New staff added to ${selectedBusiness?.businessBasic.name}. Billing has been updated.</p>`;

      try {
        await updatePaypalSubscription({
          variables: {
            subscriptionID: providerUser.subscriptionID,
            quantity: 1,
          },
        });
        await sendEmail({
          variables: {
            from: providerUser.email,
            to: email,
            subject: `Join Plany365 for ${selectedBusiness?.businessBasic.name}`,
            text: `You're invited to Plany365 for ${selectedBusiness?.businessBasic.name}: ${link}`,
            html: emailBodyNewStaff,
          },
        });
        await sendEmail({
          variables: {
            to: providerUser.email,
            subject: `Staff Added: ${selectedBusiness?.businessBasic.name}`,
            text: "Staff added and billing updated.",
            html: emailBodyBusinessOwner,
          },
        });
      } catch (error) {
        console.error("Email error:", error);
      }
    }
  };

  const handlePermissionClick = useCallback((e) => {
    setIsPopoverOpen(true);
    setAnchorEl(e.currentTarget);
  }, []);

  const handlePermissionChange = useCallback((role) => {
    setSelectedPermission(role);
    setIsPopoverOpen(false);
    setAnchorEl(null);
  }, []);

  return (
    <GenericDialog
      open={open}
      handleClose={handleClose}
      title={staff ? "Edit Staff" : "Add Staff"}
      content={
        <AddEditStaffContent
          username={username}
          setUsername={setUsername}
          usernameError={usernameError}
          email={email}
          setEmail={setEmail}
          emailError={emailError}
          url={url}
          setUrl={setUrl}
          urlError={urlError}
          role={role}
          handlePermissionClick={handlePermissionClick}
          anchorEl={anchorEl}
          isPopoverOpen={isPopoverOpen}
          selectedPermission={selectedPermission}
          handlePermissionChange={handlePermissionChange}
          selectedBusiness={selectedBusiness}
          isEdit={!!staff}
        />
      }
      actions={
        <>
          <Button onClick={handleClose} color="primary">Cancel</Button>
          <Button onClick={handleSave} color="primary" disabled={!isFormValid}>
            {staff ? "Save" : "Add"}
          </Button>
        </>
      }
    />
  );
};

export default AddEditStaffStyledDialog;
